import { forwardRef, useCallback } from 'react';

import type { SearchSuggestSchemaPatched } from '@iconik-io/iconik-api/src/schema_patched/search_suggest';

import { getSearchSuggestions } from '../../ducks/search_suggest/thunks';
import { useAppDispatch } from '../../store/react_redux_typed';
import SelectFieldMultiWrapper from '../../ui/forms/field_wrappers/select_multi/select_multi';
import type { TagSelectOption } from '../../ui/select_field/types';
import { resolveRemoteResponse } from '../../ui/select_field/utils';
import type { MetadataTagCloudSelectValue } from '../metadata_form_field/metadata_form_field';
import { SEGMENT_TYPE_TAG } from '../segment_form/constants';
import SelectFieldTagCloudValue from '../select_field_custom_values/select_field_tag_cloud_value/select_field_tag_cloud_value';
import type { SegmentInputCommonProps } from './types';

type Props = SegmentInputCommonProps & {
  segmentType: typeof SEGMENT_TYPE_TAG;
  value: MetadataTagCloudSelectValue[];
  onChange: (value: MetadataTagCloudSelectValue[]) => void;
  onValuesCopied?: () => void;
  onCopyError?: (message: string) => void;
  ref?: React.Ref<HTMLDivElement>;
};

export const SegmentInputTag = forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const dispatch = useAppDispatch();

    const handleSuggestionOptions = useCallback(
      (query: string) =>
        dispatch(
          getSearchSuggestions({
            query,
            field_name: 'metadata._gcvi_tags',
          }),
        ).then(
          resolveRemoteResponse(
            (response) => response.body.objects as SearchSuggestSchemaPatched[],
          ),
        ),
      [],
    );

    if (props.segmentType !== SEGMENT_TYPE_TAG) {
      return null;
    }

    return (
      <div ref={ref}>
        <SelectFieldMultiWrapper<
          MetadataTagCloudSelectValue,
          SearchSuggestSchemaPatched,
          TagSelectOption
        >
          name="metadata._gcvi_tags"
          required
          canClear
          isMulti
          withCheckboxSelection
          isCreatorMode
          valueComponent={SelectFieldTagCloudValue}
          value={props.value}
          optionSourceToSelect={(v) => ({
            value: v.value,
            label: v.value,
          })}
          valueSourceToSelect={(v) => ({
            score: v.score,
            value: v.value,
            label: v.value,
          })}
          valueSelectToSource={(v) => ({
            score: v.score ?? 1,
            value: v.label,
          })}
          preloadOptions
          loadOptions={handleSuggestionOptions}
          validations="isEmptyArray"
          onAfterValidation={props?.onLayoutChanged}
          onRemoteOptionsSet={props?.onLayoutChanged}
          onChange={props?.onLayoutChanged}
          enableValueCopyPaste
          onValuesCopied={props.onValuesCopied}
          onCopyError={props.onCopyError}
        />
      </div>
    );
  },
);
